<template>
  <div class="quote-annex-form">
    <!-- Information Alert -->
    <div class="alert alert-info mb-4">
      <div class="d-flex align-items-start">
        <i class="fa fa-info-circle mr-2 mt-1"></i>
        <div>
          <strong>Quote Annex Guidelines:</strong>
          <ul class="mb-0 mt-2">
            <li>If you add content to a field name, you must also provide content for that section</li>
            <li>You can remove any unwanted annex sections using the <i class="fa fa-trash text-danger"></i> button</li>
            <li>Add new fields as needed using the "Add Field" button below</li>
            <li>All fields are optional - only fill out what's relevant for your venue</li>
          </ul>
        </div>
      </div>
    </div>

    <form @submit.prevent="handleSave">
      <!-- Dynamic HTML Editor Fields -->
      <div v-for="(term, index) in localTerms" :key="`term-${index}`" class="term-section">
        
        <!-- Field Header with Actions -->
        <div class="row align-items-center mb-3" v-if="!term.isEditing">
          <div class="col-10">
            <label class="form-control-label mb-0">{{ index + 1 }}. {{ term.name }}</label>
          </div>
          <div class="col-2 text-right">
            <button 
              type="button" 
              class="btn btn-sm btn-outline-secondary mr-1"
              @click="editTermName(index)"
              title="Edit field name"
            >
              <i class="fa fa-edit"></i>
            </button>
            <button 
              type="button" 
              class="btn btn-sm btn-outline-danger"
              @click="removeTerm(index)"
              title="Remove field"
              v-if="localTerms.length > 1"
            >
              <i class="fa fa-trash"></i>
            </button>
          </div>
        </div>

        <!-- Edit Mode Header -->
        <div class="row align-items-center mb-3" v-if="term.isEditing">
          <div class="col-10">
            <label class="form-control-label mb-2">Field Name</label>
            <input 
              type="text" 
              class="form-control"
              v-model="term.name"
              @keyup.enter="saveTermName(index)"
              @blur="saveTermName(index)"
              placeholder="Enter field name"
            />
          </div>
          <div class="col-2 text-right">
            <button 
              type="button" 
              class="btn btn-sm btn-outline-success"
              @click="saveTermName(index)"
              title="Save field name"
            >
              <i class="fa fa-check"></i>
            </button>
          </div>
        </div>

        <!-- Content Editor -->
        <div class="mb-2">
          <html-editor
            :name="getFieldName(term.name)"
            :value="term.content"
            :excludedFormatters="['image']"
            @input="updateTermContent(index, $event)"
            :class="[
              'quote-annex-editor',
              { 'has-validation-error': hasValidationError(term) }
            ]"
          ></html-editor>
          <!-- Validation Error Message -->
          <div v-if="hasValidationError(term)" class="validation-error-message">
            <i class="fa fa-exclamation-triangle mr-1"></i>
            Content is required for "{{ term.name }}"
          </div>
        </div>

      </div>

      <!-- Add New Field Button -->
      <div class="text-center mt-4 mb-4">
        <button 
          type="button" 
          class="btn btn-outline-primary btn-sm"
          @click="addNewTerm"
        >
          <i class="fa fa-plus"></i> Add Field
        </button>
      </div>

    </form>
  </div>
</template>

<script>
import HtmlEditor from "@/components/Inputs/HtmlEditor.vue";

export default {
  name: "QuoteAnnexForm",
  components: {
    [HtmlEditor.name]: HtmlEditor,
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    defaultTerms: {
      type: Array,
      default: () => [
        { name: "Venue Name / ABN", content: "", order: 1 },
        { name: "Minimum Spend", content: "", order: 2 },
        { name: "Venue Hire Fee", content: "", order: 3 },
        { name: "Deposit & Payment Schedule", content: "", order: 4 },
        { name: "Cancellation Policy", content: "", order: 5 },
        { name: "Attrition / Slippage Policy", content: "", order: 6 },
        { name: "Public Holiday Surcharges", content: "", order: 7 },
        { name: "Noise / Curfew Restrictions", content: "", order: 8 },
        { name: "Bump-In / Bump-Out Rules", content: "", order: 9 },
        { name: "Security Requirements", content: "", order: 10 },
        { name: "AV & Contractor Rules", content: "", order: 11 },
        { name: "Insurance Requirements", content: "", order: 12 },
        { name: "Catering Rules", content: "", order: 13 },
        { name: "Cleaning / Damage Fees", content: "", order: 14 },
        { name: "Styling / Signage Restrictions", content: "", order: 15 }
      ]
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localTerms: [],
      isInitializing: false,
      validationErrors: []
    };
  },
  watch: {
    value: {
      handler(newValue, oldValue) {
        // Only initialize if the value actually changed and isn't the same reference
        if (newValue !== oldValue && JSON.stringify(newValue) !== JSON.stringify(this.localTerms)) {
          this.initializeTerms(newValue);
        }
      },
      immediate: true,
      deep: false // Change to false to prevent deep watching that can cause loops
    }
  },
  methods: {
    initializeTerms(terms) {
      if (this.isInitializing) return; // Prevent initialization loops
      
      this.isInitializing = true;
      try {
        if (terms && Array.isArray(terms) && terms.length > 0) {
          // Use provided terms
          this.localTerms = terms
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(term => ({ ...term, isEditing: false }));
        } else {
          // Use default structure
          this.localTerms = this.defaultTerms.map(term => ({ ...term, isEditing: false }));
        }
        this.reorderTerms();
      } catch (error) {
        console.error('Error initializing terms:', error);
        // Fallback to default terms
        this.localTerms = this.defaultTerms.map(term => ({ ...term, isEditing: false }));
        this.reorderTerms();
      } finally {
        this.isInitializing = false;
      }
    },

    updateTermContent(index, content) {
      // Update the content of the specific term
      this.localTerms[index].content = content;
      
      // Clear validation errors if content is now provided
      if (content && content.trim() !== '') {
        this.validationErrors = this.validationErrors.filter(error => 
          error.name !== this.localTerms[index].name
        );
      }
      
      this.emitUpdate();
    },

    addNewTerm() {
      if (this.readOnly) return;
      
      const newOrder = this.localTerms.length + 1;
      const newTerm = {
        name: "New Field",
        content: "",
        order: newOrder,
        isEditing: true
      };
      this.localTerms.push(newTerm);
      this.emitUpdate();
    },

    removeTerm(index) {
      if (this.readOnly) return;
      
      if (this.localTerms.length > 1) {
        this.localTerms.splice(index, 1);
        this.reorderTerms();
        this.emitUpdate();
      }
    },

    editTermName(index) {
      if (this.readOnly) return;
      
      this.localTerms[index].isEditing = true;
    },

    saveTermName(index) {
      this.localTerms[index].isEditing = false;
      if (!this.localTerms[index].name.trim()) {
        this.localTerms[index].name = "New Field";
      }
      
      // Clear validation errors for this term if name becomes empty
      if (!this.localTerms[index].name.trim()) {
        this.validationErrors = this.validationErrors.filter(error => 
          error.name !== this.localTerms[index].name
        );
      }
      
      this.emitUpdate();
    },

    reorderTerms() {
      // Update order numbers sequentially
      if (this.localTerms && Array.isArray(this.localTerms)) {
        this.localTerms.forEach((term, index) => {
          if (term) {
            term.order = index + 1;
          }
        });
      }
    },

    getFieldName(termName) {
      // Convert term name to a valid field name for the HTML editor
      return termName.toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
    },

    emitUpdate() {
      if (this.isInitializing) return; // Don't emit during initialization
      
      try {
        // Emit the updated terms without isEditing property
        const cleanTerms = this.localTerms && Array.isArray(this.localTerms) 
          ? this.localTerms.map(({ isEditing, ...term }) => term)
          : [];
        
        // Only emit if the data has actually changed
        const currentValue = JSON.stringify(cleanTerms);
        const previousValue = JSON.stringify(this.value || []);
        
        if (currentValue !== previousValue) {
          this.$emit('input', cleanTerms);
          this.$emit('change', cleanTerms);
        }
      } catch (error) {
        console.error('Error emitting update:', error);
        this.$emit('input', []);
        this.$emit('change', []);
      }
    },

    handleSave() {
      this.$emit('save', this.getCleanTerms());
    },

    getCleanTerms() {
      // Return terms without isEditing property
      try {
        return this.localTerms && Array.isArray(this.localTerms)
          ? this.localTerms.map(({ isEditing, ...term }) => term)
          : [];
      } catch (error) {
        console.error('Error getting clean terms:', error);
        return [];
      }
    },

    // Public method to get current terms data
    getTerms() {
      return this.getCleanTerms();
    },

    // Validation method to check if active annex names have content
    validateTerms() {
      const errors = [];
      const terms = this.getCleanTerms();
      
      terms.forEach((term, index) => {
        // If the name is not empty (active annex), content is mandatory
        if (term.name && term.name.trim() !== '' && (!term.content || term.content.trim() === '')) {
          errors.push({
            index: index + 1,
            name: term.name,
            message: `Content is required for "${term.name}"`
          });
        }
      });
      
      // Store validation errors for real-time display
      this.validationErrors = errors;
      
      return {
        isValid: errors.length === 0,
        errors: errors
      };
    },

    // Check if a specific term has validation errors
    hasValidationError(term) {
      return this.validationErrors.some(error => 
        error.name === term.name && 
        term.name && term.name.trim() !== '' && 
        (!term.content || term.content.trim() === '')
      );
    },

    // Clear validation errors
    clearValidationErrors() {
      this.validationErrors = [];
    }
  }
};
</script>

<style scoped>
.quote-annex-form {
  width: 100%;
}

.term-section {
  margin-bottom: 30px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  background-color: #f8f9fa;
}

.form-control-label {
  font-weight: 600;
  color: #344767;
  font-size: 14px;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 12px;
}

.btn-outline-secondary {
  border-color: #6c757d;
  color: #6c757d;
}

.btn-outline-secondary:hover {
  background-color: #6c757d;
  color: white;
}

.btn-outline-danger {
  border-color: #dc3545;
  color: #dc3545;
}

.btn-outline-danger:hover {
  background-color: #dc3545;
  color: white;
}

.btn-outline-success {
  border-color: #28a745;
  color: #28a745;
}

.btn-outline-success:hover {
  background-color: #28a745;
  color: white;
}

.btn-outline-primary {
  border-color: #007bff;
  color: #007bff;
}

.btn-outline-primary:hover {
  background-color: #007bff;
  color: white;
}

.mr-1 {
  margin-right: 0.25rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.text-center {
  text-align: center;
}

.row {
  display: flex;
  flex-wrap: wrap;
  margin-right: -15px;
  margin-left: -15px;
}

.col-2 {
  flex: 0 0 16.666667%;
  max-width: 16.666667%;
  padding-right: 15px;
  padding-left: 15px;
}

.col-10 {
  flex: 0 0 83.333333%;
  max-width: 83.333333%;
  padding-right: 15px;
  padding-left: 15px;
}

.text-right {
  text-align: right;
}

.align-items-center {
  align-items: center;
}

.form-control {
  display: block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  color: #495057;
  background-color: #fff;
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.alert {
  position: relative;
  padding: 0.75rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: 0.25rem;
}

.alert-info {
  color: #0c5460;
  background-color: #d1ecf1;
  border-color: #bee5eb;
}

.d-flex {
  display: flex;
}

.align-items-start {
  align-items: flex-start;
}

.validation-error-message {
  color: #dc3545;
  font-size: 12px;
  margin-top: 5px;
  padding: 5px 10px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 3px;
}

.text-danger {
  color: #dc3545;
}

.font-weight-bold {
  font-weight: bold;
}
</style>

<style>
/* Global styles for the quote annex editor */
.quote-annex-editor .ql-editor {
  min-height: 150px !important;
}

.quote-annex-editor .ql-container {
  height: auto !important;
}

/* Validation error styling for editor */
.has-validation-error .ql-container {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.1rem rgba(220, 53, 69, 0.25) !important;
}
</style>